/**
 * @file picking-actions-state.ts
 * @description Доступность исходов задания на сборку и подписи «почему нельзя»
 * (phase19.24 §3.3, §3.5, п. 0.4.6 фазы).
 *
 * Правило одно на весь экран: шапка, панель отметки и sticky-бар обязаны
 * одинаково отвечать на вопрос «почему кнопка серая». Раньше каждая считала
 * это сама, и недостача по строке не меняла ни шапку, ни sticky-бар.
 *
 * Блокировки в порядке важности: непривязанная позиция (чинится ЗАКАЗОМ) →
 * недостача по строкам (исход «недостача») → просто не всё собрано.
 */

import {
  formatPickedPositions,
  formatShortagePositions,
} from "../../../lib/picking-format";
import { pluralRu } from "../../../lib/plural";
import type { PickingTaskDetailDto } from "../../../lib/api-picking";

export interface PickingActionsState {
  /** Строк, закрытых недостачей. */
  shortageCount: number;
  /** Позиций заказа без фасовки (OQ-50) — проведение запрещено сервером. */
  unlinkedCount: number;
  canComplete: boolean;
  /** Почему «Провести сборку» недоступна; null — кнопка активна. */
  completeHint: string | null;
  canShortage: boolean;
  /** Почему «Оформить недостачу» недоступна; null — кнопка активна. */
  shortageHint: string | null;
}

const UNLINKED_HINT =
  "Проведение недоступно: в заказе есть позиция без товара каталога.";

/**
 * Сколько строк закрыто недостачей. Отдельный хелпер, потому что тот же
 * счёт нужен плиткам шапки (`buildPickingTaskMeta`), а два инлайн-фильтра по
 * `shortageQty` уже однажды разошлись между шапкой и sticky-баром.
 */
export function countShortageItems(task: PickingTaskDetailDto): number {
  return task.items.filter((i) => Number(i.shortageQty) > 0).length;
}

export function buildPickingActionsState(
  task: PickingTaskDetailDto,
  /** Задание живое и своё — иначе исходов нет вовсе. */
  editable: boolean,
): PickingActionsState {
  const shortageCount = countShortageItems(task);
  const unlinkedCount = task.unlinkedItems.length;
  const allPicked = task.pickedCount >= task.itemsCount;

  const canComplete = editable && allPicked && unlinkedCount === 0;
  const canShortage = editable && shortageCount > 0 && unlinkedCount === 0;

  return {
    shortageCount,
    unlinkedCount,
    canComplete,
    completeHint: canComplete
      ? null
      : unlinkedCount > 0
        ? UNLINKED_HINT
        : shortageCount > 0
          ? `Проведение недоступно: ${shortageCount} ${pluralRu(shortageCount, [
              "позиция закрыта",
              "позиции закрыты",
              "позиций закрыты",
            ])} недостачей.`
          : `Собрано ${formatPickedPositions(
              task.pickedCount,
              task.itemsCount,
            )} — проведение будет доступно, когда закроете все строки.`,
    canShortage,
    shortageHint: canShortage
      ? null
      : unlinkedCount > 0
        ? UNLINKED_HINT
        : shortageCount === 0
          ? "Недоступно: недостач по позициям нет."
          : // Недостачи есть, но задание чужое или закрыто.
            `Отмечено ${formatShortagePositions(shortageCount)} — исход оформляет исполнитель задания.`,
  };
}
